import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import CmsManager from "@/components/admin/CmsManager";
import { api, authHeader } from "@/lib/api";

const blogFields = [
  { name: "title", label: "Title", placeholder: "Blog title", required: true },
  {
    name: "excerpt",
    label: "Excerpt",
    placeholder: "Short summary",
    required: true,
    type: "textarea",
  },
  {
    name: "content",
    label: "Content",
    placeholder: "Full article body",
    required: true,
    type: "textarea",
  },
  {
    name: "image_url",
    label: "Image URL",
    placeholder: "https://...",
    required: true,
  },
  {
    name: "author",
    label: "Author",
    placeholder: "Dr. Name",
    required: true,
  },
  {
    name: "published_on",
    label: "Published On",
    placeholder: "YYYY-MM-DD",
    required: true,
  },
];

const caseFields = [
  { name: "title", label: "Title", placeholder: "Case title", required: true },
  {
    name: "category",
    label: "Category",
    placeholder: "Implantology / Cosmetic Dentistry",
    required: true,
  },
  {
    name: "summary",
    label: "Summary",
    placeholder: "Short story of case",
    required: true,
    type: "textarea",
  },
  {
    name: "treatment",
    label: "Treatment",
    placeholder: "Treatment performed",
    required: true,
  },
  {
    name: "duration",
    label: "Duration",
    placeholder: "e.g. 5 months",
    required: true,
  },
  { name: "outcome", label: "Outcome", placeholder: "Clinical result", required: true },
  {
    name: "before_image",
    label: "Before Image URL",
    placeholder: "https://...",
    required: true,
  },
  {
    name: "after_image",
    label: "After Image URL",
    placeholder: "https://...",
    required: true,
  },
];

const paperFields = [
  {
    name: "title",
    label: "Paper Title",
    placeholder: "Research paper title",
    required: true,
  },
  {
    name: "authors",
    label: "Authors",
    placeholder: "Dr. A, Dr. B",
    required: true,
  },
  {
    name: "journal",
    label: "Journal",
    placeholder: "Journal name",
    required: true,
  },
  {
    name: "published_on",
    label: "Published On",
    placeholder: "YYYY-MM-DD",
    required: true,
  },
  {
    name: "abstract",
    label: "Abstract",
    placeholder: "Brief abstract",
    required: true,
    type: "textarea",
  },
  {
    name: "pdf_url",
    label: "PDF URL",
    placeholder: "https://...",
    required: true,
  },
];

export default function AdminDashboardPage() {
  const [token, setToken] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const adminToken = localStorage.getItem("kavlekar_admin_token");
    if (!adminToken) {
      navigate("/admin/login");
      return;
    }

    const validate = async () => {
      try {
        await api.get("/admin/blogs", authHeader(adminToken));
        setToken(adminToken);
      } catch {
        localStorage.removeItem("kavlekar_admin_token");
        toast.error("Session expired, please login again");
        navigate("/admin/login");
      }
    };

    validate();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem("kavlekar_admin_token");
    navigate("/admin/login");
  };

  if (!token) {
    return (
      <main className="flex min-h-screen items-center justify-center" data-testid="admin-dashboard-loading-state">
        <p className="font-[Manrope] text-sm text-[#666666]" data-testid="admin-dashboard-loading-text">
          Preparing CMS workspace...
        </p>
      </main>
    );
  }

  return (
    <main
      className="min-h-screen bg-gradient-to-b from-[#fffefb] to-[#f9f5ec] px-6 py-8 md:px-10"
      data-testid="admin-dashboard-page"
    >
      <div className="mx-auto max-w-6xl">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1
              className="font-[Playfair_Display] text-4xl text-[#1a1a1a]"
              data-testid="admin-dashboard-title"
            >
              Kavlekar CMS Dashboard
            </h1>
            <p
              className="mt-1 font-[Manrope] text-sm text-[#666666]"
              data-testid="admin-dashboard-subtitle"
            >
              Manage blogs, patient cases, and published papers.
            </p>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="rounded-full"
            data-testid="admin-dashboard-logout-button"
          >
            <LogOut className="mr-2 h-4 w-4" /> Logout
          </Button>
        </div>

        <Tabs defaultValue="blogs" data-testid="admin-dashboard-tabs">
          <TabsList className="h-auto w-full flex-wrap gap-2 bg-[#f3ead7] p-2" data-testid="admin-dashboard-tabs-list">
            <TabsTrigger value="blogs" data-testid="admin-tab-blogs-trigger">
              Blogs
            </TabsTrigger>
            <TabsTrigger value="cases" data-testid="admin-tab-cases-trigger">
              Case Studies
            </TabsTrigger>
            <TabsTrigger value="papers" data-testid="admin-tab-papers-trigger">
              Published Papers
            </TabsTrigger>
          </TabsList>

          <TabsContent value="blogs" className="pt-4" data-testid="admin-tab-blogs-content">
            <CmsManager
              title="Blogs"
              endpoint="blogs"
              fields={blogFields}
              token={token}
              testId="admin-blogs-manager"
            />
          </TabsContent>

          <TabsContent value="cases" className="pt-4" data-testid="admin-tab-cases-content">
            <CmsManager
              title="Case Studies"
              endpoint="case-studies"
              fields={caseFields}
              token={token}
              testId="admin-case-studies-manager"
            />
          </TabsContent>

          <TabsContent value="papers" className="pt-4" data-testid="admin-tab-papers-content">
            <CmsManager
              title="Published Papers"
              endpoint="papers"
              fields={paperFields}
              token={token}
              testId="admin-papers-manager"
            />
          </TabsContent>
        </Tabs>
      </div>
    </main>
  );
}
